'use client';

/**
 * Phase 8.6 P1-A — Demo environment banner.
 *
 * Renders a sticky top banner when the caller's tenant has is_demo=true.
 * Visual cue that data is illustrative, not real, so:
 *   - sales walkthroughs can point at it,
 *   - QA / UAT testers can see they're in demo state,
 *   - if a demo tenant ever ends up with a real auth user, they cannot
 *     mistake demo content for real records.
 *
 * Source of truth is `tenants.is_demo`, fetched once on mount via the
 * authenticated supabase client. The flag is loaded server-side at signup
 * (defaults to false for new tenants); it lives in the DB so the banner
 * survives across hosts (i.e. demo tenant on app.statura.care also shows
 * the banner, not just demo.statura.care).
 */

import { useEffect, useState } from 'react';
import { Badge, Collapsible } from '@statura/ui';
import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import { createClient } from '../lib/supabase/client';

function isDemoHostname(hostname: string): boolean {
  return hostname === 'demo.statura.care' || hostname.endsWith('.demo.statura.care');
}

export function DemoEnvironmentBanner({ initialIsDemo = false }: { initialIsDemo?: boolean }) {
  const tenantId = useResolvedAuthUser()?.tenantId;
  const [isDemo, setIsDemo] = useState(initialIsDemo);
  const [hostIsDemo, setHostIsDemo] = useState(false);
  const [mounted, setMounted] = useState(initialIsDemo);

  useEffect(() => {
    const hostnameIsDemo = isDemoHostname(window.location.hostname);
    setHostIsDemo(hostnameIsDemo);
    if (hostnameIsDemo) setIsDemo(true);
    setMounted(true);
  }, []);

  useEffect(() => {
    if (initialIsDemo || !tenantId || hostIsDemo || isDemoHostname(window.location.hostname))
      return;
    let cancelled = false;
    const supabase = createClient();
    supabase
      .from('tenants')
      .select('is_demo')
      .eq('id', tenantId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data?.is_demo) setIsDemo(true);
      });
    return () => {
      cancelled = true;
    };
  }, [hostIsDemo, initialIsDemo, tenantId]);

  if (!mounted || !isDemo) return null;

  return (
    <div className="relative border-b border-status-warning-border bg-status-warning-bg px-gutter print:hidden sm:px-gutter-wide">
      <div className="flex min-h-11 min-w-0 items-center gap-2 text-status-warning-fg">
        <Badge variant="warning" dot={false} className="shrink-0 self-center font-semibold">
          Demo
        </Badge>
        <p className="min-w-0 flex-1 whitespace-nowrap text-caption lg:hidden">
          Fictional records only
        </p>
        <p className="hidden min-w-0 flex-1 whitespace-nowrap text-caption lg:block">
          Fictional records only. Do not use for real operational decisions.
        </p>
        <Collapsible
          label="Details"
          className="shrink-0 border-0 bg-transparent px-0 lg:hidden"
          triggerVariant="tertiary"
          size="compact"
          triggerClassName="w-auto gap-1 text-caption text-status-warning-fg focus:outline focus:outline-2 focus:outline-focus focus:-outline-offset-2"
          contentClassName="absolute inset-x-0 top-full z-40 border-b border-status-warning-border bg-status-warning-bg px-gutter py-3 text-body text-status-warning-fg"
        >
          All clients, staff, medications, incidents and other records are fictional. Do not use for
          real operational decisions.
        </Collapsible>
      </div>
    </div>
  );
}
